import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchConversionRates, fetchCurrencies } from "../../actions/payments";
import { fetchSupportedCountries } from "../../actions";
import { getCountryByCurrency, getCountryByISO2 } from "../../hooks/useCurrencyHelpers";

// ------------------------
// FETCH Exchange Rates
// ------------------------
export const fetchExchangeRates = createAsyncThunk("country/fetchRates", async () => {
  const res = await fetchConversionRates();

  const enrichedRates = await Promise.all(
    res.data.exchange_rates.map(async (rate) => {
      if(rate.currency_code == 'USD') return rate;
      const { country_code, currency_name } = await getCountryByCurrency(rate.currency_code);
      return {
        ...rate,
        currency_name,
        country_code,
      };
    })
  );

  return enrichedRates;
});

// ------------------------
// FETCH Allowed Countries
// ------------------------
export const fetchAllowedCountries = createAsyncThunk("country/fetchAllowedCountries", async () => {
  const response = await fetchSupportedCountries();
  const countries = response.data.countries;

  return {
    iso2Codes: countries.map((country) => country.iso2.toUpperCase()),
    fullDetails: countries,
  };
});

// ------------------------
// FETCH Currency Options
// ------------------------
// export const fetchCurrencyOptions = createAsyncThunk("country/fetchCurrencyOptions", async () => {
//   const currencyList = await fetchCurrencies();
//   const formattedOptions = [];

//   for (const currency of currencyList.data.currencies) {
//     const info = await getCountryByISO2(currency.iso2);
//     if (!info) continue;

//     formattedOptions.push({
//       id: currency.id,
//       currencyName: info.currencyName,
//       country: currency.country,
//       currencyCode: info.currencyKey,
//       name: `${info.currencyName} (${info.currencyKey})`,
//       countryCode: currency.iso2,
//       symbol: info.symbol,
//       flagUrl: info.flagUrl,
//     });
//   }

//   return formattedOptions;
// });

// ------------------------
// FETCH Currency Options (from conversion rates)
// ------------------------
export const fetchCurrencyOptions = createAsyncThunk("country/fetchCurrencyOptions", async () => {
  const res = await fetchConversionRates();
  const exchangeRates = res.data.exchange_rates;

  console.log('Exchange rates retrieved', exchangeRates)
  const formattedOptions = [];

  for (const rate of exchangeRates) {
    if(rate.currency_code == 'USD') continue;
    const { country_code, currency_name } = await getCountryByCurrency(rate.currency_code);
    if (!country_code) continue;

    const countryInfo = await getCountryByISO2(country_code);
    if (!countryInfo) continue;

    formattedOptions.push({
      // country: countryInfo.country,
      id: rate.currency_code, 
      currencyName: currency_name,
      currencyCode: rate.currency_code,
      name: `${currency_name} (${rate.currency_code})`,
      countryCode: country_code,
      symbol: countryInfo.symbol,
      flagUrl: countryInfo.flagUrl,
    });
  }
  return formattedOptions;
});


// ------------------------
// Slice
// ------------------------
const countrySlice = createSlice({
  name: "country",
  initialState: {
    exchangeRates: [],
    allowedCountries: [],
    conversionRates: [],
    currencyOptions: [],
    supportedCountryDetails: [],
    status: "idle",
  },
  reducers: {
    updateExchangeRates: (state, action) => {
      state.exchangeRates = action.payload;
    },
    updateAllowedCountries: (state, action) => {
      state.allowedCountries = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExchangeRates.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchExchangeRates.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.conversionRates = action.payload;
      })
      .addCase(fetchExchangeRates.rejected, (state) => {
        state.status = "failed";
      })

      .addCase(fetchAllowedCountries.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAllowedCountries.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.allowedCountries = action.payload.iso2Codes;
        state.supportedCountryDetails = action.payload.fullDetails;
      })
      .addCase(fetchAllowedCountries.rejected, (state) => {
        state.status = "failed";
      })

      .addCase(fetchCurrencyOptions.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCurrencyOptions.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload) {
          state.currencyOptions = action.payload;
        }
      })
      .addCase(fetchCurrencyOptions.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export const { updateExchangeRates, updateAllowedCountries } = countrySlice.actions;
export default countrySlice.reducer;